import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScrollArea } from '../ui/scroll-area';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import api from '../../services/api';
import {
    Bot,
    Send,
    Sparkles,
    Loader2,
    X,
} from 'lucide-react';

/**
 * DashboardAIAssistant - Always-available AI copilot on the dashboard.
 *
 * A floating button that opens a chat panel. The assistant is aware of the
 * user's meetings (via /assistant/dashboard) so it can answer questions about
 * what's upcoming, search past meetings, draft agendas and give tips.
 */
const SUGGESTIONS = [
    "What meetings do I have coming up?",
    "Draft an agenda for a team standup",
    "Summarize my recent meetings",
    "Suggest a name for a project kickoff",
];

const DashboardAIAssistant = ({ userName, isOpen, onOpen, onClose }) => {
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [chatHistory, setChatHistory] = useState([]);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [chatHistory, isLoading]);

    const sendMessage = useCallback(async (question) => {
        const text = (question ?? '').trim();
        if (!text || isLoading) return;

        setChatHistory(prev => [...prev, { role: 'user', content: text }]);
        setMessage('');
        setIsLoading(true);

        try {
            const response = await api.post('/assistant/dashboard', { question: text });
            setChatHistory(prev => [...prev, { role: 'assistant', content: response.data.answer }]);
        } catch (err) {
            const detail = err.response?.data?.detail || 'Failed to reach the assistant';
            setChatHistory(prev => [...prev, {
                role: 'assistant',
                content: `Sorry, I couldn't process that: ${detail}`,
                isError: true,
            }]);
        } finally {
            setIsLoading(false);
        }
    }, [isLoading]);

    return (
        <>
            {/* Floating launcher button */}
            <motion.button
                onClick={onOpen}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: isOpen ? 0 : 1, opacity: isOpen ? 0 : 1 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                data-testid="dashboard-ai-launcher"
                className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-neon-purple to-neon-cyan flex items-center justify-center shadow-2xl shadow-neon-cyan/30"
                aria-label="Open AI assistant"
            >
                <Sparkles className="w-6 h-6 text-white" />
            </motion.button>

            {/* Chat panel */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ y: 40, opacity: 0, scale: 0.95 }}
                        animate={{ y: 0, opacity: 1, scale: 1 }}
                        exit={{ y: 40, opacity: 0, scale: 0.95 }}
                        transition={{ type: 'spring', damping: 24, stiffness: 240 }}
                        className="fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] sm:w-[400px] h-[70dvh] max-h-[600px] bg-void-paper border border-white/10 rounded-2xl flex flex-col overflow-hidden shadow-2xl"
                    >
                        {/* Header */}
                        <div className="p-4 border-b border-white/5 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-neon-purple to-neon-cyan flex items-center justify-center">
                                    <Bot className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <h3 className="font-heading font-semibold text-white text-sm">AI Assistant</h3>
                                    <p className="text-xs text-grey-400">Powered by Kautilya AI</p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="text-grey-400 hover:text-white"
                                aria-label="Close assistant"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Messages */}
                        <div className="flex-1 min-h-0">
                            <ScrollArea className="h-full p-4">
                                {chatHistory.length === 0 ? (
                                    <div className="space-y-4">
                                        <div className="text-center py-4">
                                            <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-neon-purple/20 to-neon-cyan/20 flex items-center justify-center">
                                                <Sparkles className="w-8 h-8 text-neon-cyan" />
                                            </div>
                                            <h4 className="text-grey-100 font-medium mb-1">
                                                Hi {userName || 'there'}! 👋
                                            </h4>
                                            <p className="text-xs text-grey-400">
                                                Ask me about your meetings, agendas, or scheduling.
                                            </p>
                                        </div>
                                        <div className="space-y-2">
                                            <p className="text-xs text-grey-500 mb-2">Try asking:</p>
                                            {SUGGESTIONS.map((s, i) => (
                                                <button
                                                    key={i}
                                                    onClick={() => sendMessage(s)}
                                                    className="w-full text-left px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm text-grey-300 hover:text-white transition-colors"
                                                >
                                                    {s}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {chatHistory.map((msg, index) => (
                                            <motion.div
                                                key={index}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                                            >
                                                <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${msg.role === 'user'
                                                    ? 'bg-neon-cyan/20'
                                                    : 'bg-gradient-to-br from-neon-purple/30 to-neon-cyan/30'
                                                    }`}>
                                                    {msg.role === 'user' ? (
                                                        <span className="text-xs font-medium text-neon-cyan">You</span>
                                                    ) : (
                                                        <Bot className="w-4 h-4 text-neon-purple" />
                                                    )}
                                                </div>
                                                <div className={`flex-1 max-w-[280px] ${msg.role === 'user' ? 'text-right' : ''}`}>
                                                    <div className={`inline-block px-4 py-2.5 rounded-2xl ${msg.role === 'user'
                                                        ? 'bg-neon-cyan text-black rounded-br-md'
                                                        : msg.isError
                                                            ? 'bg-red-500/10 text-red-300 border border-red-500/20 rounded-bl-md'
                                                            : 'bg-white/5 text-grey-100 rounded-bl-md'
                                                        }`}>
                                                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ))}

                                        {isLoading && (
                                            <motion.div
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                className="flex gap-3"
                                            >
                                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-neon-purple/30 to-neon-cyan/30 flex items-center justify-center">
                                                    <Bot className="w-4 h-4 text-neon-purple" />
                                                </div>
                                                <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-white/5">
                                                    <div className="flex gap-1">
                                                        <span className="w-2 h-2 bg-grey-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                                        <span className="w-2 h-2 bg-grey-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                                        <span className="w-2 h-2 bg-grey-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                        <div ref={scrollRef} />
                                    </div>
                                )}
                            </ScrollArea>
                        </div>

                        {/* Input */}
                        <div className="p-4 border-t border-white/5">
                            <div className="flex gap-2">
                                <Input
                                    placeholder="Ask about your meetings..."
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage(message)}
                                    disabled={isLoading}
                                    className="input-neon text-sm"
                                />
                                <Button
                                    onClick={() => sendMessage(message)}
                                    disabled={!message.trim() || isLoading}
                                    className="bg-neon-cyan text-black hover:bg-neon-cyan/80 px-3"
                                >
                                    {isLoading ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Send className="w-4 h-4" />
                                    )}
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default DashboardAIAssistant;
